/**
 * Feature Flag Hooks
 *
 * Provides hooks for checking tenant feature flags in React components.
 * Supports both dict-based and list-based feature storage formats.
 *
 * Part of: AI Platform v5.22.0 - Phase 3.5.1 Feature Flag Enforcement
 * Updated: WQ-083 — Unified Feature Registry
 */

import { useMemo } from 'react';
import { useTenant, useTenantOptional } from '../contexts/TenantContext';
import { FEATURE_REGISTRY } from '../config/featureRegistry';

/**
 * All available feature flags with descriptions.
 * Derived from the unified feature registry (WQ-083).
 */
export const AVAILABLE_FEATURES: Record<string, string> =
  Object.fromEntries(FEATURE_REGISTRY.map(f => [f.id, f.description]));

/**
 * Check if a feature is enabled from features data.
 * Handles both dict and list formats.
 */
function isFeatureEnabled(
  features: string[] | Record<string, boolean> | null | undefined,
  feature: string
): boolean {
  if (!features) {
    return false;
  }

  // Handle dict format: { "feature": true/false }
  if (typeof features === 'object' && !Array.isArray(features)) {
    return Boolean(features[feature]);
  }

  // Handle list format: ["feature1", "feature2"]
  if (Array.isArray(features)) {
    return features.includes(feature);
  }

  return false;
}

/**
 * Hook to check if a specific feature is enabled for the current tenant.
 *
 * @param feature - The feature flag name to check
 * @returns boolean - True if the feature is enabled
 *
 * @example
 * ```tsx
 * function AIChat() {
 *   const isAIEnabled = useFeatureFlag('aiAssistant');
 *
 *   if (!isAIEnabled) {
 *     return <UpgradePrompt feature="aiAssistant" />;
 *   }
 *
 *   return <ChatInterface />;
 * }
 * ```
 */
export function useFeatureFlag(feature: string): boolean {
  const { tenant } = useTenant();

  return useMemo(() => {
    if (!tenant) {
      return false;
    }

    // Use the flexible feature check that handles both formats
    return isFeatureEnabled(tenant.features as string[] | Record<string, boolean>, feature);
  }, [tenant, feature]);
}

/**
 * Hook to check a feature flag without requiring TenantProvider.
 * Returns false if no tenant context is available.
 *
 * @param feature - The feature flag name to check
 * @returns boolean - True if the feature is enabled
 */
export function useFeatureFlagOptional(feature: string): boolean {
  const tenantContext = useTenantOptional();

  return useMemo(() => {
    if (!tenantContext?.tenant) {
      return false;
    }

    return isFeatureEnabled(
      tenantContext.tenant.features as string[] | Record<string, boolean>,
      feature
    );
  }, [tenantContext?.tenant, feature]);
}

/**
 * Hook to get all feature flags and their status for the current tenant.
 *
 * @returns Record<string, boolean> - Object mapping feature names to enabled status
 *
 * @example
 * ```tsx
 * function FeaturesList() {
 *   const features = useFeatureFlags();
 *
 *   return (
 *     <ul>
 *       {Object.entries(features).map(([name, enabled]) => (
 *         <li key={name}>
 *           {name}: {enabled ? 'Enabled' : 'Disabled'}
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useFeatureFlags(): Record<string, boolean> {
  const { tenant } = useTenant();

  return useMemo(() => {
    const result: Record<string, boolean> = {};

    for (const feature of Object.keys(AVAILABLE_FEATURES)) {
      result[feature] = isFeatureEnabled(
        tenant?.features as string[] | Record<string, boolean>,
        feature
      );
    }

    return result;
  }, [tenant?.features]);
}

/**
 * Hook to get list of enabled features for the current tenant.
 *
 * @returns string[] - Array of enabled feature names
 */
export function useEnabledFeatures(): string[] {
  const { tenant } = useTenant();

  return useMemo(() => {
    if (!tenant?.features) {
      return [];
    }

    const features = tenant.features as string[] | Record<string, boolean>;

    // Handle dict format
    if (typeof features === 'object' && !Array.isArray(features)) {
      return Object.entries(features)
        .filter(([, enabled]) => enabled)
        .map(([name]) => name);
    }

    // Handle list format
    if (Array.isArray(features)) {
      return [...features];
    }

    return [];
  }, [tenant?.features]);
}

/**
 * Hook to get list of disabled features for the current tenant.
 *
 * @returns string[] - Array of disabled feature names
 */
export function useDisabledFeatures(): string[] {
  const enabledFeatures = useEnabledFeatures();

  return useMemo(() => {
    const enabledSet = new Set(enabledFeatures);
    return Object.keys(AVAILABLE_FEATURES).filter((f) => !enabledSet.has(f));
  }, [enabledFeatures]);
}

/**
 * Hook to check multiple features at once.
 *
 * @param features - Array of feature names to check
 * @param mode - 'all' requires all features, 'any' requires at least one
 * @returns boolean - Result based on mode
 *
 * @example
 * ```tsx
 * function AdvancedDashboard() {
 *   // Requires both features
 *   const hasAdvancedAccess = useMultipleFeatures(
 *     ['advancedAnalytics', 'exportData'],
 *     'all'
 *   );
 *
 *   // Requires at least one
 *   const hasAnyDataFeature = useMultipleFeatures(
 *     ['advancedAnalytics', 'exportData'],
 *     'any'
 *   );
 * }
 * ```
 */
export function useMultipleFeatures(
  features: string[],
  mode: 'all' | 'any' = 'all'
): boolean {
  const { tenant } = useTenant();

  return useMemo(() => {
    if (!tenant?.features || features.length === 0) {
      return false;
    }

    const tenantFeatures = tenant.features as string[] | Record<string, boolean>;

    if (mode === 'all') {
      return features.every((f) => isFeatureEnabled(tenantFeatures, f));
    } else {
      return features.some((f) => isFeatureEnabled(tenantFeatures, f));
    }
  }, [tenant?.features, features, mode]);
}
